/** Real DM reply-time stats from message timestamps (not mock). */

export type DmMessageTiming = {
  senderId: string;
  timestamp: Date;
  conversationId?: string;
};

const MIN_GAP_MS = 30_000;
const MAX_GAP_MS = 7 * 24 * 60 * 60 * 1000;

function collectReplyGaps(
  sorted: DmMessageTiming[],
  fromSenderId: string,
  toSenderId: string
): number[] {
  const gaps: number[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const cur = sorted[i];
    const next = sorted[i + 1];
    if (cur.senderId !== fromSenderId || next.senderId !== toSenderId) continue;
    const gapMs = next.timestamp.getTime() - cur.timestamp.getTime();
    if (gapMs < MIN_GAP_MS || gapMs > MAX_GAP_MS) continue;
    gaps.push(gapMs / 60_000);
  }
  return gaps;
}

function average(nums: number[]): number | null {
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export function formatResponseTimeLabel(avgMinutes: number | null, replyCount: number): string {
  if (replyCount === 0 || avgMinutes == null) return 'Not enough messages';
  if (avgMinutes < 1) return 'Usually under 1 min';
  if (avgMinutes < 60) return `Usually ~${Math.max(1, Math.round(avgMinutes))} min`;
  if (avgMinutes < 24 * 60) {
    const hrs = Math.round((avgMinutes / 60) * 10) / 10;
    return hrs < 2 ? `Usually ~${hrs} hr` : `Usually ~${Math.round(avgMinutes / 60)} hrs`;
  }
  const days = Math.round((avgMinutes / (24 * 60)) * 10) / 10;
  return days < 2 ? `Usually ~${days} day` : `Usually ~${Math.round(avgMinutes / (24 * 60))} days`;
}

/** Short label for seller badges (e.g. "~23 min", "< 2 hours"). */
export function formatSellerResponseBadge(avgMinutes: number | null, replyCount: number): string | null {
  if (replyCount === 0 || avgMinutes == null || !Number.isFinite(avgMinutes)) return null;
  const m = Math.round(avgMinutes);
  if (m < 1) return '< 1 min';
  if (m < 60) return `~${m} min`;
  if (m < 120) return '< 2 hours';
  if (m < 240) return '< 4 hours';
  if (m < 720) return '< 12 hours';
  if (m < 1440) return '< 24 hours';
  const days = Math.round(m / 1440);
  return days < 2 ? '~1 day' : `~${days} days`;
}

export type DmResponseTimeResult = {
  /** How fast the other person replies after you message them. */
  partnerAvgMinutes: number | null;
  partnerReplyCount: number;
  partnerLabel: string;
  /** How fast you reply after they message you. */
  myAvgMinutes: number | null;
  myReplyCount: number;
  myLabel: string;
};

/** Per-thread reply stats between two participants. */
export function computeDmResponseTimes(
  messages: DmMessageTiming[],
  myUserId: string,
  partnerUserId: string
): DmResponseTimeResult {
  const sorted = [...messages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const partnerGaps = collectReplyGaps(sorted, myUserId, partnerUserId);
  const myGaps = collectReplyGaps(sorted, partnerUserId, myUserId);
  const partnerAvg = average(partnerGaps);
  const myAvg = average(myGaps);

  return {
    partnerAvgMinutes: partnerAvg,
    partnerReplyCount: partnerGaps.length,
    partnerLabel: formatResponseTimeLabel(partnerAvg, partnerGaps.length),
    myAvgMinutes: myAvg,
    myReplyCount: myGaps.length,
    myLabel: formatResponseTimeLabel(myAvg, myGaps.length),
  };
}

/** Average minutes for `userId` to reply after someone else messages (across threads). */
export function computeUserInboundResponseAvgMinutes(
  messages: DmMessageTiming[],
  userId: string
): { avgMinutes: number | null; replyCount: number; label: string } {
  const byConvo = new Map<string, DmMessageTiming[]>();
  for (const m of messages) {
    const key = m.conversationId || '__single__';
    const list = byConvo.get(key) || [];
    list.push({ senderId: m.senderId, timestamp: m.timestamp });
    byConvo.set(key, list);
  }

  const allGaps: number[] = [];
  for (const thread of Array.from(byConvo.values())) {
    const sorted = [...thread].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    for (let i = 0; i < sorted.length - 1; i++) {
      const cur = sorted[i];
      const next = sorted[i + 1];
      if (cur.senderId === userId || next.senderId !== userId) continue;
      const gapMs = next.timestamp.getTime() - cur.timestamp.getTime();
      if (gapMs < MIN_GAP_MS || gapMs > MAX_GAP_MS) continue;
      allGaps.push(gapMs / 60_000);
    }
  }

  const avg = average(allGaps);
  return {
    avgMinutes: avg,
    replyCount: allGaps.length,
    label: formatResponseTimeLabel(avg, allGaps.length),
  };
}
